import { useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Bot } from "lucide-react";
import { BACKEND, getOrgId, useFetch, api, formatErr, fmtTime } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function renderMd(text) {
  const html = text.replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g, '<a class="text-red-600 underline" href="$2">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>").replace(/`([^`]+)`/g, '<code class="font-mono text-xs bg-gray-100 px-1">$1</code>')
    .replace(/\n/g, "<br/>");
  return { __html: html };
}

export default function Assistant() {
  const { orgId } = useAuth();
  const { data: convs, reload } = useFetch("/assistant/conversations", [orgId]);
  const [cid, setCid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const open = async (id) => {
    try { const r = await api.get(`/assistant/conversations/${id}`); setCid(id); setMsgs(r.data); } catch (e) { toast.error(formatErr(e)); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const text = input;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/v1/assistant/chat`, { method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "X-Org-Id": getOrgId() }, body: JSON.stringify({ message: text, conversation_id: cid }) });
      if (!res.ok) throw new Error((await res.json()).detail || "Assistant unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const ev = (p.match(/^event: (.*)$/m) || [])[1];
          const data = (p.match(/^data: (.*)$/m) || [])[1];
          if (!data) continue;
          const j = JSON.parse(data);
          if (ev === "meta") setCid(j.conversation_id);
          else if (ev === "error") toast.error(j.error);
          else if (j.t) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + j.t }; return c; });
        }
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      }
      reload();
    } catch (err) {
      toast.error(err.message);
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fade-up">
      <PageHeader title="AI support assistant" crumbs={["Portal", "Assistant"]} />
      <div className="grid lg:grid-cols-4 gap-6 h-[calc(100vh-240px)] min-h-[480px]">
        <div className="card-flat p-3 overflow-y-auto hidden lg:block" data-testid="assistant-conversations">
          <button onClick={() => { setCid(null); setMsgs([]); }} className="w-full text-sm py-2 bg-gray-900 text-white mb-3" data-testid="assistant-new-chat-btn">New conversation</button>
          {(convs || []).map((c) => <button key={c.id} onClick={() => open(c.id)} className={`block w-full text-left text-sm px-2 py-2 border-l-2 ${c.id === cid ? "border-red-600 bg-gray-50" : "border-transparent hover:bg-gray-50"}`}><div className="truncate">{c.title}</div><div className="text-[11px] text-gray-400">{fmtTime(c.created_at)}</div></button>)}
        </div>
        <div className="lg:col-span-3 card-flat flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="text-sm text-gray-500"><Bot className="w-6 h-6 text-red-600 mb-2" />Ask about your devices, incidents, agent installation, Windows errors or your subscription. The assistant only sees your organization's data and cannot run actions itself.
                <div className="flex flex-wrap gap-2 mt-4">{["Which devices need attention?", "How do I install the agent on a server?", "Explain my latest incident"].map((s) => <button key={s} onClick={() => setInput(s)} className="px-3 py-1.5 border text-xs hover:border-gray-900" data-testid="assistant-suggestion">{s}</button>)}</div></div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] text-sm p-3 ${m.role === "user" ? "ml-auto bg-gray-900 text-white" : "bg-gray-50 border border-gray-200"}`} data-testid={`assistant-msg-${m.role}`}>
                {m.content ? <div dangerouslySetInnerHTML={renderMd(m.content)} /> : <Loading />}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-gray-200 p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask CodeRed…" className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-red-600" data-testid="assistant-input" />
            <button disabled={busy} className="px-4 bg-red-600 text-white disabled:opacity-50" data-testid="assistant-send-btn"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
